/**
 * Free browser speech for the first-meeting interview.
 * Web Speech API only (SpeechRecognition / webkitSpeechRecognition +
 * speechSynthesis). Not Whisper, not a paid STT vendor, not a second engine.
 */

export type BrowserSpeechRecognitionResultLike = {
  isFinal: boolean
  0: { transcript: string }
}

export type BrowserSpeechRecognitionEventLike = {
  results: ArrayLike<BrowserSpeechRecognitionResultLike>
  resultIndex: number
}

export type BrowserSpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((ev: BrowserSpeechRecognitionEventLike) => void) | null
  onerror: ((ev: { error?: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognitionLike

type SpeechWindow = Window & {
  SpeechRecognition?: SpeechRecognitionCtor
  webkitSpeechRecognition?: SpeechRecognitionCtor
}

export function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as SpeechWindow
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function canUseSpeechRecognition(): boolean {
  return getSpeechRecognitionCtor() !== null
}

export function canUseSpeechSynthesis(): boolean {
  return typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined'
}

/** Final transcripts from a recognition result event, joined with spaces. */
export function finalTranscriptFromEvent(
  ev: BrowserSpeechRecognitionEventLike,
): string {
  const parts: string[] = []
  for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
    const row = ev.results[i]
    if (row?.isFinal && row[0]?.transcript) {
      parts.push(row[0].transcript.trim())
    }
  }
  return parts.filter(Boolean).join(' ')
}

/** Append a spoken chunk onto an existing draft without inventing words. */
export function appendSpokenDraft(prev: string, spoken: string): string {
  const chunk = spoken.trim()
  if (!chunk) return prev
  const base = prev.trimEnd()
  if (!base) return chunk
  return `${base} ${chunk}`
}
